import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../../../../theme";
import { STORIES } from "../../../../data/stories.js";
import { PALETTES, resolvePalette } from "../../../../data/palettes.js";
import { hasPunct, RSVP_START_DELAY } from "./orpLogic.js";
import WordDisplay from "./WordDisplay.jsx";
import Lbl from "./controls/Lbl.jsx";
import MiniChip from "./controls/MiniChip.jsx";
import Toggle from "./controls/Toggle.jsx";
import VisionSwatch from "./controls/VisionSwatch.jsx";

export default function HeroRSVP({ ready = false }) {
  const [phase, setPhase] = useState("idle");
  const [idx, setIdx] = useState(-1);
  const [countdown, setCountdown] = useState(3);
  const [word, setWord] = useState("");
  const [storyKey, setStoryKey] = useState("tla");
  const [palette, setPalette] = useState("default");
  const [wpm, setWpm] = useState(300);
  const [orpMode, setOrpMode] = useState("natural");
  const [showGuides, setShowGuides] = useState(true);
  const [punctPause, setPunctPause] = useState(true);
  const [chunkSize, setChunkSize] = useState(1);
  const [fontSize, setFontSize] = useState(() => {
    if (typeof window === "undefined") return 72;
    const w = window.innerWidth;
    if (w < 380) return 32;
    if (w < 480) return 38;
    if (w < 600) return 46;
    if (w < 900) return 58;
    return 72;
  });

  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 860;
  });

  // Recompute fontSize on viewport resize so words always fit on the visible width
  useEffect(() => {
    if (typeof window === "undefined") return;

    const compute = () => {
      const w = window.innerWidth;

      if (w < 380) setFontSize(32);
      else if (w < 480) setFontSize(38);
      else if (w < 600) setFontSize(46);
      else if (w < 900) setFontSize(58);
      else setFontSize(72);

      setIsCompact(w < 860);
    };

    compute();
    window.addEventListener("resize", compute);

    return () => window.removeEventListener("resize", compute);
  }, []);
  const [loop, setLoop] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const timer = useRef(null);
  const containerRef = useRef(null);
  const t = useTheme();

  const story = STORIES[storyKey];
  const pal = resolvePalette(PALETTES[palette], t.mode);
  const isActive = phase === "streaming" || phase === "countdown";
  const canStop = phase === "streaming" || phase === "countdown";
  const canReset = phase !== "idle";

  const reset = useCallback(() => {
    clearInterval(timer.current); clearTimeout(timer.current);
    setPhase("idle"); setIdx(-1); setCountdown(3); setWord("");
  }, []);

  const stop = useCallback(() => {
    clearInterval(timer.current);
    clearTimeout(timer.current);

    if (phase === "countdown") {
      setPhase("idle");
      setCountdown(3);
      setWord("");
      setIdx(-1);
      return;
    }

    if (phase === "streaming") {
      setPhase("stopped");
    }
  }, [phase]);

  const buildChunks = useCallback(() => {
    const w = story.words;
    if (chunkSize <= 1) return w;
    const chunks = [];
    for (let i = 0; i < w.length; i += chunkSize) chunks.push(w.slice(i, i + chunkSize).join(" "));
    return chunks;
  }, [story, chunkSize]);

  const startStream = useCallback(() => {
    const chunks = buildChunks();
    let i = 0;
    setPhase("streaming"); setIdx(0); setWord(chunks[0]);
    const baseMs = Math.round(60000 / wpm);

    const next = () => {
      i++;
      if (i >= chunks.length) {
        clearTimeout(timer.current);
        if (loop) { i = 0; setIdx(0); setWord(chunks[0]); timer.current = setTimeout(next, baseMs); }
        else { setPhase("done"); }
        return;
      }
      setIdx(i); setWord(chunks[i]);
      const delay = punctPause && hasPunct(chunks[i]) ? baseMs * 1.8 : baseMs;
      timer.current = setTimeout(next, delay);
    };
    const firstDelay = punctPause && hasPunct(chunks[0]) ? baseMs * 1.8 : baseMs;
    timer.current = setTimeout(next, firstDelay);
  }, [buildChunks, wpm, punctPause, loop]);

  const startCountdown = useCallback(() => {
    setPhase("countdown"); setCountdown(3);
    let c = 3;
    const tick = () => { c--; if (c <= 0) { startStream(); return; } setCountdown(c); timer.current = setTimeout(tick, 380); };
    timer.current = setTimeout(tick, 380);
  }, [startStream]);

  const handleStart = () => { if (!isActive) { reset(); setTimeout(startCountdown, 40); } };
  const handleStory = (k) => { reset(); setStoryKey(k); };

  // Auto-start — waits for `ready` (loading screen done) + RSVP_START_DELAY
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  useEffect(() => {
    if (!ready || hasAutoStarted || phase !== "idle") return;
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAutoStarted && phase === "idle") {
          setHasAutoStarted(true);
          setTimeout(() => { reset(); setTimeout(startCountdown, 40); }, RSVP_START_DELAY);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [ready, hasAutoStarted, phase, reset, startCountdown]);

  useEffect(() => () => { clearInterval(timer.current); clearTimeout(timer.current); }, []);

  const chunks = buildChunks();
  const pct = phase === "done" ? 100 : phase === "streaming" ? ((idx + 1) / chunks.length) * 100 : 0;

  return (
    <div ref={containerRef} style={{
      width: "100%",
      maxWidth: "min(90vw, 1100px)",
      margin: "0 auto",
      position: "relative",
      padding: "0 clamp(16px, 4vw, 48px)",
      "--pal-pivot": pal.pivot,
      "--pal-pivot-glow": `rgba(${pal.glow},0.35)`,
    }}>
      {/* Ambient glow tied to palette */}
      <div style={{
        position: "absolute", top: "-30%", left: "50%", transform: "translateX(-50%)",
        width: "160%", height: "100%", pointerEvents: "none",
        background: `radial-gradient(ellipse at 50% 80%, rgba(${pal.glow},0.04) 0%, transparent 55%)`,
        transition: "background 0.8s ease",
      }} />

      {/* ═══ EYEBROW: Story Selection ═══ */}
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 10,
        marginBottom: "clamp(16px, 4vw, 36px)",
        position: "relative",
      }}>
        <Lbl>Story Selection</Lbl>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: "clamp(8px, 1.5vw, 12px)",
          flexWrap: "wrap",
        }}>
          {Object.entries(STORIES).map(([k, s]) => (
            <MiniChip key={k} label={s.title} active={storyKey === k}
              onClick={() => handleStory(k)} color={pal.pivot} size="lg" />
          ))}
        </div>
      </div>

      {/* ═══ READER AREA — no borders, no card ═══ */}
      <div style={{ position: "relative", padding: "clamp(24px, 5vw, 48px) 0 clamp(20px, 4vw, 32px)" }}>

        {/* States */}
        {phase === "countdown" && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: fontSize * 1.8 }}>
            <span key={countdown} style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: fontSize * 0.85, fontWeight: 300, color: t.textFaint,
              animation: "rsvpPulse 0.3s ease-out",
            }}>{countdown}</span>
          </div>
        )}
        {phase === "idle" && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: fontSize * 1.8 }}>
            <span style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 14, color: t.textMid, letterSpacing: "0.12em",
            }}>ready</span>
          </div>
        )}
        {(phase === "streaming" || phase === "done" || phase === "stopped") && (
          <WordDisplay word={word} phase={phase} pal={pal} orpMode={orpMode} showGuides={showGuides} fontSize={fontSize} />
        )}

        {/* Progress */}
        <div style={{ marginTop: 24, padding: "0 2px" }}>
          <div style={{ height: 2, borderRadius: 1, background: t.borderSubtle, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 1, background: pal.pivot,
              width: `${pct}%`, opacity: 0.6,
              transition: phase === "streaming" ? "width 0.12s linear" : "width 0.4s ease",
            }} />
          </div>
          <div style={{
            display: "flex", justifyContent: "space-between", marginTop: 6,
            fontFamily: "'DM Mono', monospace",
            fontSize: "clamp(13px, 1vw, 14px)", color: t.textMid, letterSpacing: "0.05em",
            fontVariantNumeric: "tabular-nums",
          }}>
            <span>{phase === "streaming" || phase === "done" ? idx + 1 : "—"}</span>
            <span>{wpm} wpm · {chunks.length} {chunkSize > 1 ? "chunks" : "words"}</span>
          </div>
        </div>

        {/* Done reveal */}
        {phase === "done" && (
          <div style={{
            textAlign: "center", marginTop: 20,
            fontSize: "clamp(13px, 1.2vw, 14px)",
            fontFamily: "'DM Mono', monospace",
            color: t.textMid, letterSpacing: "0.03em",
            lineHeight: 1.8, animation: "fadeUp 0.5s ease-out",
            maxWidth: "min(90%, 600px)", margin: "20px auto 0", fontStyle: "italic",
            padding: "0 12px",
          }}>"{story.words.join(" ")}"</div>
        )}

        {/* Playback buttons */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: 10,
          flexWrap: "wrap",
          marginTop: "clamp(20px, 3vw, 32px)",
        }}>
          <button
            onClick={handleStart}
            disabled={isActive}
            style={{
              background: isActive ? t.surface : `rgba(${pal.glow},0.1)`,
              border: `1px solid ${isActive ? t.borderSubtle : `rgba(${pal.glow},0.28)`}`,
              borderRadius: 100,
              padding: "clamp(10px, 1.5vw, 14px) clamp(22px, 3vw, 34px)",
              color: isActive ? t.textGhost : pal.pivot,
              fontFamily: "'DM Mono', monospace",
              fontSize: "clamp(13px, 1.2vw, 14px)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: isActive ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              fontWeight: 500,
              minHeight: 44,
            }}
          >
            {phase === "done" || phase === "stopped" ? "↻ replay" : "▶ start"}
          </button>

          <button
            onClick={stop}
            disabled={!canStop}
            style={{
              background: canStop ? `rgba(${pal.glow},0.08)` : t.surface,
              border: `1px solid ${canStop ? `rgba(${pal.glow},0.22)` : t.borderSubtle}`,
              borderRadius: 100,
              padding: "clamp(10px, 1.5vw, 14px) clamp(22px, 3vw, 34px)",
              color: canStop ? pal.pivot : t.textGhost,
              fontFamily: "'DM Mono', monospace",
              fontSize: "clamp(13px, 1.2vw, 14px)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: canStop ? "pointer" : "not-allowed",
              transition: "all 0.3s ease",
              fontWeight: 500,
              minHeight: 44,
            }}
          >
            ■ stop
          </button>

          <button
            onClick={reset}
            disabled={!canReset}
            style={{
              background: canReset ? "transparent" : t.surface,
              border: `1px solid ${canReset ? t.borderSubtle : t.borderSubtle}`,
              borderRadius: 100,
              padding: "clamp(10px, 1.5vw, 14px) clamp(22px, 3vw, 34px)",
              color: canReset ? t.textLow : t.textGhost,
              fontFamily: "'DM Mono', monospace",
              fontSize: "clamp(13px, 1.2vw, 14px)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: canReset ? "pointer" : "not-allowed",
              transition: "all 0.3s ease",
              fontWeight: 500,
              minHeight: 44,
            }}
          >
            reset
          </button>
        </div>
      </div>

      {/* ═══ PRIMARY CONTROLS ═══ */}
      <div style={{
        marginTop: 12,
        borderTop: "1px solid " + t.borderSubtle,
        paddingTop: 14,
      }}>
        <div style={{
          display: "flex", flexWrap: "wrap", gap: "clamp(12px, 2vw, 22px)",
          alignItems: "start",
        }}>
          {/* Speed */}
          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
            <Lbl>Speed</Lbl>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <input type="range" min={80} max={800} step={20} value={wpm}
                disabled={isActive}
                onChange={(e) => setWpm(Number(e.target.value))}
                style={{ flex: 1, accentColor: pal.pivot, opacity: isActive ? 0.2 : 1, cursor: isActive ? "not-allowed" : "pointer" }}
              />
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: t.textMid, fontWeight: 500, minWidth: 28, textAlign: "right" }}>{wpm}</span>
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 5, flexWrap: "wrap" }}>
              {[150, 300, 500, 800].map((v) => (
                <MiniChip key={v} label={v} active={wpm === v} disabled={isActive}
                  onClick={() => { if (!isActive) setWpm(v); }} color={pal.pivot} />
              ))}
            </div>
          </div>

          {/* Fixation */}
          <div style={{ flex: "0 0 auto" }}>
            <Lbl>Fixation</Lbl>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
              <Toggle on={showGuides} onToggle={() => setShowGuides(!showGuides)} color={pal.pivot} />
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, color: t.textLow }}>Guides</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ EXPANDABLE: Control Surfaces + Rods & Cones ═══ */}
      <div style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        marginTop: 8,
        borderTop: "1px solid " + t.borderSubtle,
      }}>
        <button onClick={() => setExpanded(!expanded)} style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: "12px 0",
          background: "none", border: "none", cursor: "pointer", color: t.text,
          fontFamily: "'DM Mono', monospace",
          transition: "all 0.2s ease",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = t.surfaceHover; }}
          onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
        >
          <span style={{
            fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase",
            color: expanded ? pal.pivot : t.textMid,
            fontWeight: 500, transition: "color 0.3s ease",
          }}>Control Surfaces + Rods & Cones</span>
          <span style={{
            fontSize: 14, color: expanded ? pal.pivot : t.textMid,
            transform: expanded ? "rotate(180deg)" : "rotate(0)",
            transition: "all 0.3s ease", display: "inline-block",
          }}>▾</span>
        </button>

        <div style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          maxHeight: expanded ? 1200 : 0,
          overflow: expanded ? "visible" : "hidden",
          transition: `max-height ${expanded ? "0.6s cubic-bezier(0.16, 1, 0.3, 1)" : "0.4s ease-in"}`,
        }}>
          <div style={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            borderTop: "1px solid " + t.borderSubtle,
          }}>

            {/* Control Surfaces */}
            <div style={{ padding: "12px 0 10px" }}>
              <Lbl style={{ marginBottom: 8 }}>Control Surfaces</Lbl>

              <div style={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "1fr"
                  : "repeat(3, minmax(0, 1fr))",
                alignItems: "stretch",
                gap: 1,
                marginTop: 8,
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid " + t.borderSubtle,
              }}>
                {/* ORP Mode */}
                <div style={{ padding: "10px 12px", background: t.cardBg }}>
                  <Lbl>ORP Mode</Lbl>
                  <div style={{ display: "flex", gap: 4, marginTop: 7, flexWrap: "wrap" }}>
                    {["natural", "start", "center", "end"].map((m) => (
                      <MiniChip key={m} label={m} active={orpMode === m}
                        onClick={() => setOrpMode(m)} color={pal.pivot} />
                    ))}
                  </div>
                </div>

                {/* Presentation */}
                <div style={{ padding: "10px 12px", background: t.cardBg }}>
                  <Lbl>Presentation</Lbl>
                  <div style={{ marginTop: 7 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: t.textFaint, display: "block", marginBottom: 4 }}>Chunk size</span>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[1, 2, 3].map((n) => (
                        <MiniChip key={n} label={`${n}w`} active={chunkSize === n} disabled={isActive}
                          onClick={() => { if (!isActive) setChunkSize(n); }} color={pal.pivot} />
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: t.textFaint, display: "block", marginBottom: 4 }}>Font scale</span>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[{ l: "S", v: 38 }, { l: "M", v: 58 }, { l: "L", v: 84 }].map(({ l, v }) => (
                        <MiniChip key={l} label={l} active={fontSize === v}
                          onClick={() => setFontSize(v)} color={pal.pivot} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Behaviour */}
                <div style={{
                  minWidth: 0,
                  padding: "10px 12px",
                  background: t.cardBg,
                }}>
                  <Lbl style={{ marginBottom: 7 }}>Behaviour</Lbl>
                  <div style={{ display: "flex", gap: "clamp(12px, 2vw, 24px)", alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Toggle on={punctPause} onToggle={() => setPunctPause(!punctPause)} color={pal.pivot} />
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: t.textLow }}>Punctuation pause</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Toggle on={loop} onToggle={() => setLoop(!loop)} color={pal.pivot} />
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: t.textLow }}>Loop</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ height: 1, background: t.borderSubtle }} />

            {/* Rods & Cones */}
            <div style={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              padding: "14px 0 12px",
            }}>
              <Lbl style={{ marginBottom: 10 }}>Rods & Cones · Aka Accessibility</Lbl>
              <div style={{
                width: "100%",
                display: "flex",
                gap: 10,
                marginTop: 8,
                flexWrap: "wrap",
                alignItems: "flex-end",
              }}>
                {Object.entries(PALETTES).map(([k, scheme]) => (
                  <VisionSwatch key={k} schemeKey={k} scheme={scheme}
                    active={palette === k} onSelect={() => setPalette(k)} />
                ))}
              </div>
              <div style={{
                fontFamily: "'DM Mono', monospace",
                marginTop: 8, fontSize: 9, color: t.textFaint,
                letterSpacing: "0.04em", lineHeight: 1.5,
              }}>
                Active: <span style={{ color: pal.pivot, fontWeight: 500 }}>{pal.name}</span>
                {palette !== "default" && ` — ${pal.desc.split("—")[0].trim()}`}
              </div>
            </div>

            <div style={{ height: 1, background: t.borderSubtle }} />

            {/* Concepts */}
            <div style={{ padding: "12px 0 16px" }}>
              <Lbl style={{ marginBottom: 8 }}>Concepts</Lbl>
              <div style={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                gap: 1,
                borderRadius: 8,
                overflow: "hidden",
                marginTop: 8,
                border: "1px solid " + t.borderSubtle,
              }}>
                {[
                  { k: "ORP", d: "The highlighted pivot letter is where your eye naturally fixates. Shifting it changes how the brain decodes each word." },
                  { k: "Fixation", d: "Guide lines anchor your gaze to a single point. Content passes through you — not the other way around." },
                  { k: "Chunking", d: "Multi-word chunks trade raw speed for comprehension. Working memory buffers 2–3 words before ORP loses its effect." },
                ].map((item) => (
                  <div key={item.k} style={{ padding: "12px 14px", background: t.cardBg }}>
                    <div style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 12, fontWeight: 600, letterSpacing: "0.10em",
                      color: pal.pivot, marginBottom: 6,
                      transition: "color 0.4s ease",
                      textTransform: "uppercase",
                    }}>{item.k}</div>
                    <div style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 14, color: t.textMid, lineHeight: 1.6,
                      letterSpacing: "0.01em",
                    }}>{item.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
